import { NgIf } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  HostListener,
  effect,
  inject,
  signal,
} from "@angular/core";
import { IconButtonComponent } from "app/core/ui";
import { LinkFailureStore } from "../link-failure.store";
import { AutoHideTimerService } from "./auto-hide-timer.service";

/** Delay before the banner hides itself. */
export const LINK_FAILURE_BANNER_TIMEOUT_MS = 10_000;
/** Duration of the "copied" confirmation on the copy button. */
export const COPY_FEEDBACK_DURATION_MS = 2_000;

export type CopyState = "idle" | "copied" | "failed";

/**
 * A banner telling the user that a link could not be opened by the host (e.g. popup blocked),
 * and letting them copy the address. It hides itself after a delay, unless it is hovered or focused.
 */
@Component({
  selector: "[linkFailureBanner]",
  templateUrl: "./template.html",
  styleUrls: ["./style.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, IconButtonComponent],
  providers: [AutoHideTimerService],
})
export class LinkFailureBannerComponent {
  private readonly store = inject(LinkFailureStore);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly autoHide = inject(AutoHideTimerService);

  private copyTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly failure = this.store.failure;
  protected readonly copyState = signal<CopyState>("idle");

  protected readonly message = $localize`:@@link-failure.message:`;
  protected readonly copyLabel = $localize`:@@link-failure.copy:`;
  protected readonly copiedLabel = $localize`:@@link-failure.copied:`;
  protected readonly copyFailedLabel = $localize`:@@link-failure.copy-failed:`;

  @HostBinding("class") hostClasses =
    "border rounded align-items-center gap-3 p-3 d-flex bg-body-secondary text-body";
  @HostBinding("attr.role") hostRole = "status";
  @HostBinding("class.d-none") get shouldHide() {
    return this.failure() === null;
  }

  constructor() {
    // A new failure (even for the same URL) restarts the whole feedback cycle.
    effect(
      () => {
        const current = this.failure();
        this.clearCopyTimer();
        this.copyState.set("idle");
        if (current === null) {
          this.autoHide.stop();
        } else {
          this.autoHide.start(LINK_FAILURE_BANNER_TIMEOUT_MS, () => this.dismiss());
        }
      },
      { allowSignalWrites: true }
    );

    inject(DestroyRef).onDestroy(() => {
      this.clearCopyTimer();
      this.store.dismiss();
    });
  }

  @HostListener("mouseenter")
  protected onMouseEnter(): void {
    this.autoHide.pause("pointer");
  }

  @HostListener("focusin")
  protected onFocusIn(): void {
    this.autoHide.pause("focus");
  }

  @HostListener("mouseleave")
  protected onMouseLeave(): void {
    this.autoHide.resume("pointer");
  }

  @HostListener("focusout", ["$event"])
  protected onFocusOut(event: FocusEvent): void {
    // Moving the focus between the banner's own controls must not resume the timer.
    if (
      event.relatedTarget instanceof Node &&
      this.host.nativeElement.contains(event.relatedTarget)
    ) {
      return;
    }
    this.autoHide.resume("focus");
  }

  @HostListener("keydown.escape")
  protected onEscape(): void {
    this.dismiss();
  }

  /** Copies the URL to the clipboard and reflects the outcome on the button. */
  protected async copy(url: string): Promise<void> {
    this.clearCopyTimer();
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Also covers a missing `navigator.clipboard`: the user is asked to copy manually.
      this.copyState.set("failed");
      return;
    }
    this.copyState.set("copied");
    this.copyTimer = setTimeout(() => this.copyState.set("idle"), COPY_FEEDBACK_DURATION_MS);
  }

  /** Closes the banner. */
  protected dismiss(): void {
    this.autoHide.stop();
    this.clearCopyTimer();
    this.store.dismiss();
  }

  private clearCopyTimer(): void {
    if (this.copyTimer !== null) {
      clearTimeout(this.copyTimer);
      this.copyTimer = null;
    }
  }
}
